import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardHeader, StatTile, TrendPill } from '../../../components/ui/Card';
import { SegmentedControl, type TabItem } from '../../../components/ui/Tabs';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { InfoTip } from '../../../components/ui/InfoTip';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { formatDayMonth, formatDayMonthYear } from '../../../utils/dateFormat';
import { HealthView } from '../../Dashboard/gtm/HealthView';
import { Select } from '../../../components/ui/Input';
import {
  CollapsibleSection,
  DAY_RANGES,
  DEFAULT_MARKETING_RANGE,
  MARKETING_RANGE_OPTIONS,
  RANGE_PARAM,
  Toolbar,
  UnavailableSources,
  isDayRange,
  isoDaysAgo,
  isoToday,
  rangeLabel,
  type DayRange,
} from '../shared';
import { Table, THead, Th, Td, Tr, Nil, TableFooter, isConstant } from '../../../components/ui/Table';
import {
  ChartCard,
  ChartEmpty,
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_LEGEND,
  CHART_TOOLTIP,
  RAMP_INK,
} from '../../../components/ui/chart';
import { useSearchVisibility } from '../../../hooks/useSearchVisibility';
import type {
  SearchVisibilityData,
  SearchVisibilityGranularity,
  SearchVisibilityWeekRow,
  SearchVisibilityDomainRow,
  SearchVisibilityKeywordRow,
  SearchVisibilityLandingPageRow,
  SearchVisibilityMarketSummary,
} from '../../../api/search-visibility';
import {
  fmtAvgPositionChange,
  fmtPath,
  fmtPoints,
  fmtPosition,
  fmtPositionChange,
  fmtTraffic,
  fmtTrafficDelta,
  fmtTrafficFine,
  fmtTrafficFineDelta,
  fmtVisibility,
  fmtWeek,
  fmtChangeWindow,
  harvestFreshness,
  recentVisibilityChange,
  isFlatVisibilityChange,
  peakWeek,
} from './format';

/**
 * The chart resolution follows the period — daily points up to a month,
 * weekly beyond that — instead of a second Weekly/Daily toggle.
 */
function granularityFor(startDate: string, endDate: string): SearchVisibilityGranularity {
  const days = (Date.parse(endDate) - Date.parse(startDate)) / 86_400_000;
  return days <= 31 ? 'daily' : 'weekly';
}

type RangePreset = DayRange | 'custom';
const RANGE_OPTIONS: readonly TabItem<RangePreset>[] = [...MARKETING_RANGE_OPTIONS, { id: 'custom', label: 'Custom' }];

/**
 * The shared Marketing period plus a custom range, in the same `?range=custom&cstart=&cend=`
 * params Overview uses, so a custom range carries between the two tabs.
 */
function useVisibilityPeriod() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(RANGE_PARAM);
  const range: RangePreset = raw === 'custom' || isDayRange(raw) ? raw : DEFAULT_MARKETING_RANGE;
  const customStart = searchParams.get('cstart') || isoDaysAgo(30);
  const customEnd = searchParams.get('cend') || isoToday();
  const setParam = (key: string, value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );
  return range === 'custom'
    ? { range, startDate: customStart, endDate: customEnd, label: `${formatDayMonthYear(customStart)} to ${formatDayMonthYear(customEnd)}`, setParam }
    : { range, startDate: isoDaysAgo(DAY_RANGES[range]), endDate: isoToday(), label: rangeLabel(range), setParam };
}

function PeriodControl({ period }: { period: ReturnType<typeof useVisibilityPeriod> }) {
  const input = 'h-8 rounded border border-border bg-surface-primary px-2 text-body-xs text-text-primary';
  return (
    <>
      {period.range === 'custom' && (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            aria-label="Start date"
            value={period.startDate}
            max={period.endDate}
            onChange={(e) => e.target.value && period.setParam('cstart', e.target.value)}
            className={input}
          />
          <span className="text-body-xs text-text-tertiary">to</span>
          <input
            type="date"
            aria-label="End date"
            value={period.endDate}
            min={period.startDate}
            max={isoToday()}
            onChange={(e) => e.target.value && period.setParam('cend', e.target.value)}
            className={input}
          />
        </div>
      )}
      <SegmentedControl ariaLabel="Period" options={RANGE_OPTIONS} value={period.range} onChange={(id) => period.setParam(RANGE_PARAM, id)} />
    </>
  );
}

/**
 * Competitor lines drawn on the trend chart, beyond our own.
 *
 * Capped at the six ink/stone styles (three greys, solid then dashed) so no two
 * domains share a line; the tracked set is twenty-one domains, and the
 * leaderboard below the chart carries every one numerically.
 */
const TREND_COMPETITORS = 6;

/** "2026-09-08" → "8 Sep" for a dense time axis. */
function fmtAxisDate(date: string): string {
  return formatDayMonth(date);
}

function deltaChange(d: { text: string; trend: 'up' | 'down' | 'neutral' }) {
  return { value: d.text, trend: d.trend };
}

const MARKET_PARAM = 'market';

/** The default market: the one with the most tracked keywords, so it doesn't change with the period. */
function defaultMarket(campaigns: SearchVisibilityData['campaigns']): string | null {
  const best = [...campaigns].sort((a, b) => (b.keywordsCount ?? 0) - (a.keywordsCount ?? 0) || a.market.localeCompare(b.market))[0];
  return best?.campaignId ?? null;
}

export function SearchVisibilityView({ toolbarStart }: { toolbarStart?: ReactNode }) {
  const periodState = useVisibilityPeriod();
  const { startDate, endDate, label: period } = periodState;
  // The market lives in ?market= so it survives a period change and can be linked. Until 13 Oct
  // it was local state and the API's default followed the period (Germany at 90 days, NL at 7).
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignId = searchParams.get(MARKET_PARAM) ?? undefined;
  const setCampaignId = useCallback(
    (id: string) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(MARKET_PARAM, id);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const query = useSearchVisibility(campaignId, startDate, endDate, granularityFor(startDate, endDate));
  const { data, isLoading, error } = query;

  // No market chosen: the stable default (most tracked keywords), whatever the period.
  useEffect(() => {
    if (campaignId || !data || data.campaigns.length === 0) return;
    const preferred = defaultMarket(data.campaigns);
    if (preferred && preferred !== data.campaignId) setCampaignId(preferred);
  }, [campaignId, data, setCampaignId]);

  return (
    <div className="space-y-6">
      <Toolbar
        start={
          <>
            {toolbarStart}
            <p className="text-body-xs text-text-tertiary">From Semrush Position Tracking</p>
            {data && <HarvestFreshness data={data} />}
            <InfoTip label="About search visibility">
              <SourceNotes data={data} />
            </InfoTip>
          </>
        }
        end={<PeriodControl period={periodState} />}
      />

      {error ? (
        <ErrorState title="Couldn’t load search visibility" error={error} onRetry={() => query.refetch()} />
      ) : isLoading && !data ? (
        <LoadingState rows={4} />
      ) : data ? (
        <SearchVisibilityBody data={data} onSelectMarket={setCampaignId} period={period} />
      ) : null}

      <CollapsibleSection title="Tech / SEO health" description="Index coverage, Core Web Vitals, Lighthouse, AI-bot access">
        <HealthView startDate={startDate} endDate={endDate} periodLabel={period} />
      </CollapsibleSection>
    </div>
  );
}

/**
 * "Latest harvest 7 Oct · refreshed weekly". Amber once the harvest is older
 * than the weekly pull allows, and a link to Integrations when it is stale or
 * the Semrush sync is failing, since that is where it is checked and re-run.
 */
function HarvestFreshness({ data }: { data: SearchVisibilityData }) {
  const f = harvestFreshness(data.lastCapturedDate, data.sync);
  if (f.tone === 'ok') {
    return <p className="text-body-xs text-text-tertiary" title={f.title}>· {f.label}</p>;
  }
  const badge = (
    <span title={f.title}>
      <Badge variant={f.tone === 'failing' ? 'error' : 'warning'}>{f.label}</Badge>
    </span>
  );
  return f.linkToIntegrations ? (
    <Link to="/integrations" className="hover:opacity-80" title={`${f.title} Open Integrations to check the Semrush sync.`}>
      {badge}
    </Link>
  ) : (
    badge
  );
}

function SearchVisibilityBody({
  data,
  onSelectMarket,
  period,
}: {
  data: SearchVisibilityData;
  onSelectMarket: (campaignId: string) => void;
  period: string;
}) {
  const selected = data.campaigns.find((c) => c.campaignId === data.campaignId);

  if (data.campaigns.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No tracked markets yet"
          description="The Semrush position-tracking pull runs about weekly (it spends API units). Run it now from Automations → “Semrush: Position Tracking Sync” or Settings → Integrations → Semrush."
        />
      </Card>
    );
  }

  const avgDays = data.averageDays ?? null;
  // A short period averages one harvest day at each end: say so, not "1-day average".
  const avgNote = avgDays && avgDays > 1 ? `${avgDays}-day average` : 'latest harvest day';
  // The tiles' change window in dates, so it reads against the weekly headline above them.
  const changeWindow = fmtChangeWindow(data.firstCapturedDate, data.lastCapturedDate, avgDays);
  // Every change tile names the same two windows, so none reads as "Last 90 days" while comparing two weeks.
  const windowNote = !changeWindow
    ? `${period} · first vs last harvest`
    : avgDays && avgDays > 1
      ? `${changeWindow} · ${avgDays}-day averages`
      : `${changeWindow} · single harvest days`;
  const changeNote = avgDays
    ? ` The change compares the average of the period's last ${avgDays} harvest days with its first ${avgDays}${changeWindow ? ` (${changeWindow})` : ''}, so one odd day can't swing it. The line above the tiles compares whole weeks instead.`
    : '';

  return (
    <div className="space-y-6">
      <UnavailableSources sources={data.unavailable} />

      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="sv-market" className="sr-only">Market</label>
        <Select
          id="sv-market"
          value={data.campaignId ?? ''}
          onChange={(e) => onSelectMarket(e.target.value)}
        >
          {data.campaigns.map((c) => (
            <option key={c.campaignId} value={c.campaignId}>
              {c.market}
            </option>
          ))}
        </Select>
        <span className="text-body-sm text-text-tertiary">
          {selected?.keywordsCount ?? 0} tracked keywords
        </span>
      </div>

      <RecentChange
        weeks={data.weeks}
        periodChange={
          data.kpis
            ? {
                points: data.kpis.visibilityChange,
                level: data.kpis.visibility,
                window: changeWindow,
                top10: data.kpis.top10,
                lastHarvest: data.lastCapturedDate,
              }
            : null
        }
      />

      {data.kpis && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatTile
            label="SERP visibility"
            value={fmtVisibility(data.kpis.visibility)}
            change={visibilityTileChange(data.kpis.visibilityChange, data.kpis.visibility)}
            sub={`${windowNote} · Semrush`}
            tooltip={`Share of possible Google clicks won by the tracked keywords in this market (Semrush). Change is in percentage points; under a quarter of a point (or 5% of the level) it is shown uncoloured, as about flat.${changeNote} Not the same as AI visibility (Peec).`}
          />
          <StatTile
            label="SERP share of voice"
            value={fmtVisibility(data.kpis.shareOfVoice)}
            change={deltaChange(fmtPoints(data.kpis.shareOfVoiceChange))}
            sub={`${windowNote} · Semrush`}
            tooltip={`Our share of the tracked competitor set's Google visibility (Semrush).${changeNote} Not AI share of voice.`}
          />
          <StatTile
            label="Avg position"
            value={fmtPosition(data.kpis.avgPosition)}
            change={deltaChange(fmtAvgPositionChange(data.kpis.avgPositionChange))}
            sub={`${windowNote} · tracked keywords · Semrush`}
            tooltip={`Average across all tracked keywords; lower is better. Keywords ranking nowhere count as 100.${changeNote} Differs from the Search Console average, which only counts queries we appeared for.`}
          />
          <StatTile
            label="Est. traffic"
            value={fmtTraffic(data.kpis.estimatedTraffic)}
            change={deltaChange(fmtTrafficDelta(data.kpis.estimatedTrafficChange))}
            sub={`${windowNote} · Semrush estimate`}
            tooltip={`Semrush's own traffic estimate for these keywords, not sessions from our analytics. It is the daily Visibility report's estimate, averaged; Top landing pages is a separate Semrush report on one harvest day, so the two don't add up.${changeNote}`}
          />
          <StatTile
            label="Top 3 / top 10"
            value={`${data.kpis.top3} / ${data.kpis.top10}`}
            sub={`${data.lastCapturedDate ? `Harvest of ${formatDayMonth(data.lastCapturedDate)}` : 'Latest harvest'} · ${data.kpis.keywordsTracked} tracked · ${data.kpis.entered} entered, ${data.kpis.left} left`}
          />
        </div>
      )}

      <MarketStrip
        markets={data.markets}
        activeCampaignId={data.campaignId}
        onSelect={onSelectMarket}
        windowNote={windowNote}
        avgNote={avgNote}
      />

      <WeeklyHistory weeks={data.weeks} />
      <TrendChart data={data} />
      <Leaderboard rows={data.leaderboard} market={selected?.market ?? ''} avgNote={avgNote} />
      <RankDistribution data={data} />

      <div className="grid gap-6 xl:grid-cols-2">
        <KeywordTable rows={data.keywords} />
        <LandingPageTable
          rows={data.landingPages}
          snapshotDate={data.lastCapturedDate}
          tileTraffic={data.kpis?.estimatedTraffic ?? null}
          avgNote={avgNote}
        />
      </div>

    </div>
  );
}

/**
 * The page's lead: SERP visibility in the last full week against four weeks
 * before it, from the weekly rows. Loud (amber) when it fell SHARP_DROP or
 * more. A partial newest week is left out so a 3-day week can't fake a slide;
 * the tiles below compare the period's first and last days and say so in dates.
 */
function RecentChange({
  weeks,
  periodChange,
}: {
  weeks: SearchVisibilityWeekRow[] | null;
  /** The SERP visibility tile's change over the whole period, so the two read together. */
  periodChange: PeriodChange | null;
}) {
  const c = recentVisibilityChange(weeks ?? []);
  if (!c) return null;
  const pct = c.relative === null ? '' : ` (${c.relative > 0 ? '+' : '−'}${Math.round(Math.abs(c.relative) * 100)}%)`;
  const top10 =
    c.from.top10 !== null && c.to.top10 !== null ? ` Keywords in the top 10: ${c.from.top10} → ${c.to.top10}.` : '';
  const text = `Last full week vs four weeks earlier: SERP visibility ${fmtVisibility(c.from.visibility)} (${fmtWeek(c.from.weekStart, c.from.weekEnd)}) → ${fmtVisibility(c.to.visibility)} (${fmtWeek(c.to.weekStart, c.to.weekEnd)}), ${fmtPoints(c.points).text}${pct}.${top10}`;
  const arc = periodArc(weeks ?? [], c, periodChange);
  const note = 'Weekly averages, partial weeks left out · Semrush.';
  if (c.sharpDrop) {
    return (
      <Alert variant="warning" title="SERP visibility dropped sharply">
        {text}
        {arc && ` ${arc}`} {note}
      </Alert>
    );
  }
  return (
    <p className="text-body-sm text-text-secondary">
      {text}
      {arc && ` ${arc}`} <span className="text-text-tertiary">{note}</span>
    </p>
  );
}

/**
 * Why the tile and the headline can point different ways: the tile compares the
 * period's ends (15–21 Jul vs 1–7 Oct: +0.12 pts, flat), the headline the last
 * five weeks (−28%). Said in one sentence with the peak in between, so the two
 * don't read as a contradiction. Null when they agree in direction.
 */
function periodArc(
  weeks: SearchVisibilityWeekRow[],
  recent: NonNullable<ReturnType<typeof recentVisibilityChange>>,
  period: PeriodChange | null,
): string | null {
  if (!period || period.points === null || !period.window) return null;
  const flat = isFlatVisibilityChange(period.points, period.level);
  const [latest, earliest] = period.window.split(' vs ');
  const move = flat ? `about flat (${fmtPoints(period.points).text})` : fmtPoints(period.points).text;
  const peak = peakWeek(weeks);
  const sameWay = !flat && Math.sign(period.points) === Math.sign(recent.points);
  const peakText =
    !sameWay && peak && peak.visibility !== null && peak.visibility > recent.to.visibility
      ? `, with a peak of ${fmtVisibility(peak.visibility)} in ${fmtWeek(peak.weekStart, peak.weekEnd)} between`
      : '';
  const top10 = period.top10 !== null && period.lastHarvest ? `; top 10 on ${formatDayMonth(period.lastHarvest)}: ${period.top10}` : '';
  // Named in full so the tiles' figures (other weeks) can't read as contradicting the weekly figures.
  return `The tiles below cover other days: ${latest} averages ${fmtVisibility(period.level)}, ${move} against ${earliest}${peakText}${top10}.`;
}

type PeriodChange = {
  points: number | null;
  level: number | null;
  window: string | null;
  top10: number | null;
  lastHarvest: string | null;
};

/** The SERP visibility tile's change, uncoloured when it is too small to call. */
function visibilityTileChange(change: number | null, level: number | null) {
  const d = fmtPoints(change);
  return { value: d.text, trend: isFlatVisibilityChange(change, level) ? ('neutral' as const) : d.trend };
}

/** Every market at a glance — the view the weekly emails never gave us. */
function MarketStrip({
  markets,
  activeCampaignId,
  onSelect,
  windowNote,
  avgNote,
}: {
  markets: SearchVisibilityMarketSummary[];
  activeCampaignId: string | null;
  onSelect: (campaignId: string) => void;
  /** The tiles' change window ("1–7 Oct vs 15–21 Jul · 7-day averages"). */
  windowNote: string;
  avgNote: string;
}) {
  const withRank = markets.filter((m) => m.rank !== null);
  // "Rank 1 / 21" on every row: the set size is said once in the header instead.
  const sameSetSize = withRank.length > 0 && withRank.every((m) => m.competitorCount === withRank[0]!.competitorCount);
  const setSize = sameSetSize ? withRank[0]!.competitorCount : null;
  return (
    <Card>
      <CardHeader
        title="All markets"
        description={`Our SERP visibility (${avgNote}) and rank in the tracked competitor set, per market (Semrush). Change: ${windowNote}. Click a row to open it.`}
      />
      <MarketExceptions markets={markets} />
      {markets.length === 0 ? (
        <ChartEmpty label="No market data yet." />
      ) : (
        <Table minWidth={640}>
            <THead>
              <tr>
                <Th>Market</Th>
                <Th align="right">SERP visibility</Th>
                <Th align="right" title={`Percentage points, ${windowNote}`}>Change</Th>
                <Th align="right">Est. traffic</Th>
                <Th align="right">{setSize ? `Rank of ${setSize}` : 'Rank'}</Th>
              </tr>
            </THead>
            <tbody>
              {markets.map((m) => {
                return (
                  <Tr
                    key={m.campaignId}
                    onClick={() => onSelect(m.campaignId)}
                    className={m.campaignId === activeCampaignId ? 'bg-surface-secondary' : undefined}
                  >
                    <Td>
                      <span className="font-medium text-text-primary">{m.market}</span>
                    </Td>
                    <Td align="right"><Cell value={fmtVisibility(m.visibility)} /></Td>
                    <Td align="right">
                      <VisibilityDelta change={m.visibilityChange} level={m.visibility} />
                    </Td>
                    <Td align="right"><Cell value={fmtTraffic(m.estimatedTraffic)} /></Td>
                    <Td align="right">
                      {m.rank === null ? <Nil /> : setSize ? m.rank : `${m.rank} / ${m.competitorCount}`}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
      )}
    </Card>
  );
}

/**
 * The exception line above the markets table: how many markets lost visibility
 * and the biggest drop ("7 of 8 markets down · France −3.20 pts"). Nothing when
 * no market is down.
 */
function MarketExceptions({ markets }: { markets: SearchVisibilityMarketSummary[] }) {
  const known = markets.filter((m) => m.visibilityChange !== null);
  const down = known.filter((m) => (m.visibilityChange ?? 0) < 0 && !isFlatVisibilityChange(m.visibilityChange, m.visibility));
  if (down.length === 0) return null;
  // One threshold for count and row colour: a fall under the flat band reads grey in the table, so it is
  // counted as flat here, and said, instead of vanishing from the count.
  const flatDown = known.filter((m) => (m.visibilityChange ?? 0) < 0 && isFlatVisibilityChange(m.visibilityChange, m.visibility));
  const worst = [...down].sort((a, b) => (a.visibilityChange ?? 0) - (b.visibilityChange ?? 0))[0]!;
  return (
    <p className="mb-4 flex flex-wrap items-center gap-2 text-body-sm text-text-secondary">
      <span className="font-medium text-text-primary">
        {down.length} of {known.length} markets down
      </span>
      {flatDown.length > 0 && (
        <>
          <span className="text-text-tertiary">·</span>
          <span title="A fall under 0.25 pts (or 5% of the level) is too small to call, shown grey in the table">
            {flatDown.length} slightly lower, about flat
          </span>
        </>
      )}
      <span className="text-text-tertiary">·</span>
      <span>biggest drop {worst.market}</span>
      <TrendPill trend="down">{fmtPoints(worst.visibilityChange).text}</TrendPill>
    </p>
  );
}

/**
 * A change pill; no change at all ("0", "—") is an empty cell, not a grey "→ 0" on every row.
 * `neutral` keeps the arrow but drops the good/bad colour — a competitor gaining is not good news.
 */
function DeltaText({ delta, neutral = false }: { delta: { text: string; trend: 'up' | 'down' | 'neutral' }; neutral?: boolean }) {
  if (delta.text === '—' || delta.trend === 'neutral') return <Nil />;
  return (
    <TrendPill trend={delta.trend} tone={neutral ? 'neutral' : undefined}>
      {delta.text}
    </TrendPill>
  );
}

/**
 * A SERP visibility change in points, by one rule everywhere on the page: too
 * small to call (isFlatVisibilityChange) reads as plain grey text, not a green
 * or red pill ("−0.09 pts" sat red in the weekly table on 13 Oct).
 */
function VisibilityDelta({ change, level, neutral = false }: { change: number | null; level: number | null; neutral?: boolean }) {
  const delta = fmtPoints(change);
  if (delta.trend !== 'neutral' && isFlatVisibilityChange(change, level)) {
    return <span className="tabular-nums text-text-tertiary" title="Too small to call: about flat">{delta.text}</span>;
  }
  return <DeltaText delta={delta} neutral={neutral} />;
}

/** A formatted value, or the faint empty-cell dot when the formatter gave "—". */
function Cell({ value }: { value: string | number | null }) {
  return value === null || value === '—' ? <Nil /> : <>{value}</>;
}

/**
 * Week-by-week history — the "how did it move over the last weeks" view.
 *
 * Newest week first, because the standing question is what changed this week.
 * Each row averages the week's harvest days: single days swung 7.9 → 30.9 → 7.8%
 * from one week to the next.
 */
function WeeklyHistory({ weeks }: { weeks: SearchVisibilityWeekRow[] | null }) {
  const list = weeks ?? [];
  const pager = usePagination(list, 15);
  // The same rank every week ("1 / 21" on each row) is said once below the table.
  const sameRank = isConstant(list, (w) => `${w.rank ?? ''}/${w.competitorCount}`);
  const first = list[0];
  return (
    <Card>
      <CardHeader
        title="Week by week"
        description="Our SERP visibility averaged over each whole week's harvest days (Monday to Sunday, even when the period starts mid-week), newest first: change is vs the previous week. Top 10 is on the week's last harvest day. A partial week has fewer than 7 harvest days."
      />
      {!weeks || weeks.length === 0 ? (
        <ChartEmpty label="No weekly history for this market yet." />
      ) : (
        <>
          <Table minWidth={480}>
            <THead>
              <tr>
                <Th>Week</Th>
                <Th align="right">SERP visibility</Th>
                <Th align="right">Δ</Th>
                <Th align="right">Top 10</Th>
                {!sameRank && <Th align="right">Rank</Th>}
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((w) => (
                <Tr key={w.weekStart}>
                  <Td>
                    <span className="text-text-primary">{fmtWeek(w.weekStart, w.weekEnd)}</span>
                    {w.harvestDays !== undefined && w.harvestDays < 7 && (
                      <span className="text-body-xs text-text-tertiary">
                        {' '}· partial, {w.harvestDays} {w.harvestDays === 1 ? 'day' : 'days'}
                      </span>
                    )}
                  </Td>
                  <Td align="right"><Cell value={fmtVisibility(w.visibility)} /></Td>
                  <Td align="right">
                    <VisibilityDelta change={w.visibilityChange} level={w.visibility} />
                  </Td>
                  <Td align="right"><Cell value={w.top10} /></Td>
                  {!sameRank && (
                    <Td align="right">
                      {w.rank === null ? <Nil /> : `${w.rank} / ${w.competitorCount}`}
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
          {sameRank && first && (
            <TableFooter>
              {first.rank === null
                ? 'No rank reading in any week.'
                : `Rank ${first.rank} of ${first.competitorCount} tracked domains every week.`}
            </TableFooter>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * Visibility over time, us against the strongest competitors.
 *
 * Series are chosen by latest visibility but coloured by a stable slot, so
 * changing the market or the range never repaints a domain that stayed on the
 * chart. Our own line is always first and always drawn.
 */
function TrendChart({ data }: { data: SearchVisibilityData }) {
  const { series, rows } = useMemo(() => {
    const leaderboard = data.leaderboard ?? [];
    const self = leaderboard.find((r) => r.isSelf);
    const competitors = leaderboard
      .filter((r) => !r.isSelf && r.visibility !== null)
      .slice(0, TREND_COMPETITORS);
    const picked = [...(self ? [self.domain] : []), ...competitors.map((r) => r.domain)];

    const points = (data.trend ?? []).map((p) => {
      const row: Record<string, string | number | null> = { date: p.date };
      for (const domain of picked) row[domain] = p.domains[domain] ?? null;
      return row;
    });
    return { series: picked, rows: points };
  }, [data.leaderboard, data.trend]);

  const selfDomain = data.leaderboard?.find((r) => r.isSelf)?.domain;

  return (
    <ChartCard
      title="SERP visibility over time"
      description="Percentage of possible Google clicks won, us against the strongest tracked competitors (Semrush)"
      height={CHART_HEIGHT.tall}
      isEmpty={rows.length === 0 || series.length === 0}
      emptyLabel="No visibility history for this market yet."
      chart={
        <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="date" tickFormatter={fmtAxisDate} {...CHART_AXIS} minTickGap={24} />
          <YAxis unit="%" {...CHART_AXIS} />
          <Tooltip
            {...CHART_TOOLTIP}
            labelFormatter={(label) => fmtAxisDate(String(label))}
            formatter={(value, name) => [
              fmtVisibility(typeof value === 'number' ? value : null),
              name,
            ]}
          />
          {/* itemSorter=null keeps declaration order — recharts sorts legend
              labels alphabetically by default, which would scramble the
              us-first, then-strongest ordering the series are built in. */}
          {/* Line-shaped legend icons carry each competitor's dash pattern, so the key
              matches the chart; circles would hide the only difference between two greys. */}
          <Legend {...CHART_LEGEND} iconType="plainline" iconSize={18} itemSorter={null} />
          {series.map((domain, i) => (
            <Line
              key={domain}
              type="linear"
              dataKey={domain}
              name={domain === selfDomain ? 'You' : domain}
              stroke={domain === selfDomain ? '#121212' : competitorStyle(i - (selfDomain ? 1 : 0)).stroke}
              strokeDasharray={domain === selfDomain ? undefined : competitorStyle(i - (selfDomain ? 1 : 0)).dash}
              strokeWidth={domain === selfDomain ? 3.5 : 1.75}
              dot={false}
              connectNulls isAnimationActive={false}
            />
          ))}
        </LineChart>
      }
      footer={
        series.length > 0 ? (
          <p className="mt-2 text-body-xs text-text-tertiary">
            Showing our domain plus the {Math.max(series.length - 1, 0)} strongest competitors. Every
            tracked domain is in the leaderboard below.
          </p>
        ) : undefined
      }
    />
  );
}

/**
 * Our own line is ink and thick; each competitor gets its own muted hue (none is
 * ink, indigo or a status colour), plus a dash pattern, so the key and the lines
 * stay readable in print too.
 */
const COMPETITOR_STYLES = [
  { stroke: '#B8863B', dash: undefined }, // ochre
  { stroke: '#5B7C99', dash: undefined }, // steel blue
  { stroke: '#8E5B7A', dash: '6 3' }, // plum
  { stroke: '#6B8E8A', dash: '2 2' }, // muted teal
  { stroke: '#7A6A58', dash: '8 3 2 3' }, // umber
  { stroke: '#A8A29A', dash: '4 4' }, // stone
] as const;

function competitorStyle(i: number) {
  return COMPETITOR_STYLES[i % COMPETITOR_STYLES.length]!;
}

/** The email's Visibility + Traffic tables, in one sortable-by-visibility list. */
function Leaderboard({ rows, market, avgNote }: { rows: SearchVisibilityDomainRow[] | null; market: string; avgNote: string }) {
  const pager = usePagination(rows ?? [], 15);
  return (
    <Card>
      <CardHeader
        title="Competitor leaderboard"
        description={`${market ? `Tracked competitor set in ${market}` : 'Tracked competitor set'} · ${avgNote}`}
      />
      {!rows || rows.length === 0 ? (
        <ChartEmpty label="No competitor data for this market yet." />
      ) : (
        <>
        <Table minWidth={720}>
            <THead>
              <tr>
                <Th>Domain</Th>
                <Th align="right">SERP visibility</Th>
                <Th align="right">Δ</Th>
                <Th align="right">Est. traffic</Th>
                <Th align="right">Δ</Th>
                <Th align="right" title="Average Semrush position across tracked keywords">Avg pos</Th>
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => (
                <Tr key={r.domain} className={r.isSelf ? 'bg-surface-secondary' : undefined}>
                  <Td>
                    <span
                      className={
                        r.isSelf ? 'font-semibold text-text-primary' : 'text-text-secondary'
                      }
                    >
                      {r.isSelf ? 'You' : r.domain}
                    </span>
                    {r.isSelf && <span className="ml-2 text-body-xs text-text-tertiary">{r.domain}</span>}
                  </Td>
                  <Td align="right"><Cell value={fmtVisibility(r.visibility)} /></Td>
                  <Td align="right">
                    <VisibilityDelta change={r.visibilityChange} level={r.visibility} neutral={!r.isSelf} />
                  </Td>
                  <Td align="right"><Cell value={fmtTraffic(r.estimatedTraffic)} /></Td>
                  <Td align="right">
                    <DeltaText delta={fmtTrafficDelta(r.estimatedTrafficChange)} neutral={!r.isSelf} />
                  </Td>
                  <Td align="right"><Cell value={fmtPosition(r.avgPosition)} /></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
        </>
      )}
    </Card>
  );
}

/**
 * Where our keywords sit, over time.
 *
 * Buckets are parts of one whole (every tracked keyword lands in exactly one),
 * so they use a single-hue ramp rather than categorical colours — strongest for
 * the top-3 bucket, fading out to "not ranking".
 */
function RankDistribution({ data }: { data: SearchVisibilityData }) {
  const buckets = [
    { key: 'top3', label: 'Top 3' },
    { key: 'top4to10', label: '4–10' },
    { key: 'top11to20', label: '11–20' },
    { key: 'top21to100', label: '21–100' },
    { key: 'notRanking', label: 'Not ranking' },
  ] as const;

  const rows = data.rankDistribution ?? [];

  return (
    <ChartCard
      title="Rank distribution"
      description="Tracked keywords by position bucket: derived from the stored positions, so it always matches the keyword table"
      height={CHART_HEIGHT.default}
      isEmpty={rows.length === 0}
      emptyLabel="No keyword history for this market yet."
      chart={
        <AreaChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="date" tickFormatter={fmtAxisDate} {...CHART_AXIS} minTickGap={24} />
          <YAxis {...CHART_AXIS} />
          <Tooltip {...CHART_TOOLTIP} labelFormatter={(label) => fmtAxisDate(String(label))} />
          {/* Same reason as the trend chart: without this the buckets list as
              "11–20, 21–100, 4–10, Not ranking, Top 3". */}
          <Legend {...CHART_LEGEND} itemSorter={null} />
          {buckets.map((b, i) => (
            <Area
              key={b.key}
              type="linear"
              dataKey={b.key}
              name={b.label}
              stackId="ranks"
              stroke={RAMP_INK[i]}
              fill={RAMP_INK[i]}
              fillOpacity={1}
              // A 2px surface-coloured stroke keeps stacked bands from bleeding
              // into each other where two adjacent ramp steps are close.
              strokeWidth={2} isAnimationActive={false}
            />
          ))}
        </AreaChart>
      }
    />
  );
}

function KeywordTable({ rows }: { rows: SearchVisibilityKeywordRow[] | null }) {
  const list = rows ?? [];
  const pager = usePagination(list, 15);
  // Semrush tags repeat on nearly every row ("GLOSSARY"): counted once below, not a pill per row.
  const tagCounts = new Map<string, number>();
  for (const r of list) for (const t of r.tags) tagCounts.set(t, (tagCounts.get(t) ?? 0) + 1);
  const tagNote = [...tagCounts.entries()].map(([t, n]) => `${n} tagged “${t}”`).join(', ');
  return (
    <Card>
      <CardHeader title="Tracked keywords" description="Best positions first; change across the period" />
      {!rows || rows.length === 0 ? (
        <ChartEmpty label="No keyword data for this market yet." />
      ) : (
        <>
          <Table minWidth={420}>
            <THead>
              <tr>
                <Th>Keyword</Th>
                <Th align="right">Pos</Th>
                <Th align="right">Change</Th>
                <Th align="right">Volume</Th>
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => (
                <Tr key={r.keyword}>
                  <Td>
                    <span className="text-text-secondary">{r.keyword}</span>
                  </Td>
                  <Td align="right"><Cell value={r.position} /></Td>
                  <Td align="right">
                    <DeltaText delta={fmtPositionChange(r.change)} />
                  </Td>
                  <Td align="right"><Cell value={r.searchVolume} /></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
          {tagNote && <TableFooter>Semrush tags: {tagNote}, of {list.length} keywords.</TableFooter>}
        </>
      )}
    </Card>
  );
}

/**
 * Semrush's landing-pages report on the latest harvest day. It spreads a
 * market's estimate thinly (25 pages from 0.1 to 1.1 visits on 7 Oct), so it
 * shows one decimal instead of whole visits, which rounded 24 of 25 pages to 0
 * and hid them. Only pages with no estimate at all are left out.
 */
function LandingPageTable({
  rows,
  snapshotDate,
  tileTraffic,
  avgNote,
}: {
  rows: SearchVisibilityLandingPageRow[] | null;
  snapshotDate: string | null;
  tileTraffic: number | null;
  avgNote: string;
}) {
  // Semrush spreads fractions of a visit over long-tail pages ("0.1"): under half a visit is noise.
  const shown = useMemo(
    () => (rows ?? []).filter((r) => r.estimatedTraffic !== null && r.estimatedTraffic >= MIN_LANDING_VISITS),
    [rows],
  );
  const hidden = (rows?.length ?? 0) - shown.length;
  const total = (rows ?? []).reduce((n, r) => n + (r.estimatedTraffic ?? 0), 0);
  const pager = usePagination(shown, 15);
  const day = snapshotDate ? formatDayMonth(snapshotDate) : 'the latest harvest';
  return (
    <Card>
      <CardHeader
        title="Top landing pages"
        description={`Snapshot of ${day} · Semrush landing-pages report · est. visits per page`}
      />
      {shown.length === 0 ? (
        <ChartEmpty
          label={
            hidden > 0
              ? `None of the ${hidden} landing pages in the ${day} snapshot reaches half an estimated visit.`
              : 'No landing-page data for this market yet.'
          }
        />
      ) : (
        <>
          <Table minWidth={420} colWidths={['auto', '5rem', '5.5rem', '5.5rem']}>
            <THead>
              <tr>
                <Th>Page</Th>
                <Th align="right">Traffic</Th>
                <Th align="right">Change</Th>
                <Th align="right">Keywords</Th>
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => (
                <Tr key={r.url}>
                  <Td>
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block break-words text-text-secondary hover:text-text-primary hover:underline"
                      title={r.url}
                    >
                      {fmtPath(r.url)}
                    </a>
                  </Td>
                  <Td align="right"><Cell value={fmtTrafficFine(r.estimatedTraffic)} /></Td>
                  <Td align="right">
                    <DeltaText delta={fmtTrafficFineDelta(r.estimatedTrafficChange)} />
                  </Td>
                  <Td align="right"><Cell value={r.keywordsCount} /></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
          <TableFooter>
            {fmtTrafficFine(total)} est. visits on {day}, {shown.length} {shown.length === 1 ? 'page' : 'pages'} listed
            {hidden > 0 ? ` (${hidden} under half an estimated visit, or none, left out)` : ''}. The Est. traffic tile (
            {fmtTraffic(tileTraffic)}, {avgNote}) comes from Semrush&apos;s Visibility report, a different model, so the
            two don&apos;t add up.
          </TableFooter>
        </>
      )}
    </Card>
  );
}

/** Landing pages under this many estimated visits are left out of the list (counted in its footer). */
const MIN_LANDING_VISITS = 0.5;

/** Where the numbers come from — lives in the (i) next to the controls. */
function SourceNotes({ data }: { data?: SearchVisibilityData }) {
  return (
    <div className="space-y-2">
      <p>
        Semrush Position Tracking: how visible we are against the tracked competitor set, per market. Pulled about
        every {data?.sync?.refreshEveryDays ?? 6} days at 05:00 UTC, to save Semrush API units, so the latest harvest
        is normally up to a week old.
        {data?.lastCapturedDate
          ? ` Latest harvest for this market: ${formatDayMonthYear(data.lastCapturedDate)}.`
          : ''}
      </p>
      <p>
        SERP visibility and SERP share-of-voice changes are percentage points. They measure Google results, not AI
        answers: AI visibility comes from Peec on the AI assistants side. Estimated traffic is Semrush&apos;s own model,
        not sessions from our analytics.
      </p>
      <p>
        {data?.firstCapturedDate
          ? `History reaches back to ${formatDayMonthYear(data.firstCapturedDate)}, which is everything Semrush retains for this campaign. `
          : ''}
        Weekly points and rows average each week&apos;s harvest days.
        {data?.averageDays
          ? ` Headline figures, markets and the leaderboard average the period's last ${data.averageDays} harvest days, and their changes compare that with the first ${data.averageDays}.`
          : ''}
      </p>
    </div>
  );
}
